"use client"

import Link from "next/link"
import React from "react"
import { TIconButton } from "../button/TIconButton"
import { TMenu } from "../menu/TMenu"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../shadcn/breadcrumb"
import { DropdownMenuItem } from "../shadcn/dropdown-menu"

/**
 * @description 네비게이션 브레드크럼. 경로 계층 표시. 3개 초과 시 중간 항목은 드롭다운 메뉴로 축약
 * @example
 * <TBreadcrumb
 *   items={[
 *     { name: "홈", href: "/" },
 *     { name: "카테고리", href: "/category" },
 *     { name: "현재 페이지", href: "/category/page" },
 *   ]}
 * />
 */
interface TBreadcrumbProps {
	/** 경로 항목 배열 (순서대로 계층 표시) */
	items: Array<{
		/** 표시 이름 */
		name: string
		/** 링크 경로 */
		href: string
	}>
	/** 추가 CSS 클래스 */
	className?: string
}

export const TBreadcrumb = React.memo((props: TBreadcrumbProps) => {
	const { items, className } = props

	const breadcrumbData = React.useMemo(() => {
		const isMore = items.length > 3
		const parentLink = items.length > 0 ? items[0] : undefined
		const currentLink = items.length > 0 ? items[items.length - 1] : undefined
		const menuList = isMore ? items.slice(1, items.length - 1) : []

		return { isMore, parentLink, currentLink, menuList }
	}, [items])

	if (items.length === 0) return null

	const { isMore, parentLink, currentLink, menuList } = breadcrumbData

	return (
		<Breadcrumb className={className}>
			<BreadcrumbList>
				{isMore && parentLink && (
					<>
						<BreadcrumbItem>
							<BreadcrumbLink href={parentLink.href}>{parentLink.name}</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
					</>
				)}

				{isMore && menuList.length > 0 && (
					<>
						<BreadcrumbItem>
							<TMenu
								renderButton={({ open }) => <TIconButton name="moreHorizontal" size="small" color={open ? "tertiary" : "ghost"} />}
								renderMenu={() => (
									<>
										{menuList.map((item) => (
											<DropdownMenuItem key={item.name} asChild>
												<Link href={item.href}>{item.name}</Link>
											</DropdownMenuItem>
										))}
									</>
								)}
							/>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
					</>
				)}

				{isMore && currentLink && (
					<BreadcrumbItem>
						<BreadcrumbPage>{currentLink.name}</BreadcrumbPage>
					</BreadcrumbItem>
				)}

				{!isMore && (
					<>
						{items.map((item, index) => (
							<React.Fragment key={item.name}>
								<BreadcrumbItem>
									{index === items.length - 1 ? <BreadcrumbPage>{item.name}</BreadcrumbPage> : <BreadcrumbLink href={item.href}>{item.name}</BreadcrumbLink>}
								</BreadcrumbItem>
								{index < items.length - 1 && <BreadcrumbSeparator />}
							</React.Fragment>
						))}
					</>
				)}
			</BreadcrumbList>
		</Breadcrumb>
	)
})

TBreadcrumb.displayName = "TBreadcrumb"
